import React, { useState } from 'react';
import { X, User } from 'lucide-react';

const UserModal = ({ apiCall, roles, editingUser, onClose, onSuccess, showError }) => {
  const [modalData, setModalData] = useState({
    username: editingUser?.Username || '',
    fullName: editingUser?.FullName || '',
    email: editingUser?.Email || '',
    password: '',
    roles: editingUser?.Roles ? editingUser.Roles.split(',') : []
  });
  const [loading, setLoading] = useState(false);

  // Handle case where roles is undefined
  if (!roles || roles.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-red-600">Error</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          <p className="text-gray-700 mb-4">
            Unable to load roles. Please try refreshing the page or contact support.
          </p>
          <button 
            onClick={onClose}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleRoleToggle = (roleName) => {
    setModalData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleName)
        ? prev.roles.filter(r => r !== roleName)
        : [...prev.roles, roleName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!modalData.username.trim()) {
      showError('Username is required');
      return;
    }

    if (!editingUser && !modalData.password.trim()) {
      showError('Password is required for new users');
      return;
    }

    if (modalData.roles.length === 0) {
      showError('Please select at least one role');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        username: modalData.username.trim(),
        fullName: modalData.fullName.trim(),
        email: modalData.email.trim() || null,
        roles: modalData.roles
      };

      if (modalData.password.trim()) {
        payload.password = modalData.password;
      }

      if (editingUser) {
        await apiCall(`/users/${editingUser.UserID}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        await apiCall('/users', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      
      onSuccess();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <User className="text-blue-600" size={24} />
            {editingUser ? 'Edit User' : 'Create User'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username *
              </label>
              <input
                type="text"
                required
                value={modalData.username}
                onChange={(e) => setModalData({...modalData, username: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter username"
                disabled={!!editingUser}
              />
              {editingUser && (
                <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={modalData.fullName}
                onChange={(e) => setModalData({...modalData, fullName: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                required={!editingUser}
                value={modalData.password}
                onChange={(e) => setModalData({...modalData, password: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={modalData.email}
                onChange={(e) => setModalData({...modalData, email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Roles * (Select at least one)
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-gray-50">
              {roles.map(role => (
                <label 
                  key={role.RoleID} 
                  className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={modalData.roles.includes(role.RoleName)}
                    onChange={() => handleRoleToggle(role.RoleName)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">{role.RoleName}</p>
                    {role.Description && (
                      <p className="text-xs text-gray-600">{role.Description}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Selected: {modalData.roles.length > 0 ? modalData.roles.join(', ') : 'None'}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;